<template>
  <div :class="collapsedView ? 'd-flex flex-column' : 'd-flex align-center'">
    <!-- Title with external link icon -->
    <div class="pricing-group-title d-flex align-center" @click.stop="selectUnit">
      {{ unit.levelEntryDescription }}
      <v-icon v-if="!preventUnitSelection" small class="ml-2" color="primary">
        mdi-open-in-new
      </v-icon>
    </div>

    <div v-if="!isSelectable" class="d-flex ml-3 align-center">
      <div v-for="(icon, index) in kpiData" :key="index" class="mr-1">
        <work-packages-unit-kpi-icon-new
          :number="icon.number"
          :tooltip-message="icon.tooltipMessage"
          :rejected="icon.rejected"
          :user-role="icon.userRole"
          :shape="icon.shape"
          :bg-color="icon.bgColor"
          :text-color="icon.textColor"
          :outline-color="icon.outlineColor"
        />
      </div>
      <div class="mr-1">
        <approval-icon-new
          :approved="unitComplete"
          :unit-level="true"
          :tooltip-message="$t('workPackages.unitComplete')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import { get, size, every, filter, isEmpty, some } from 'lodash';
import colours from '../../../ow-colors';

export default {
  name: 'PricingGroupSummary',

  props: {
    unit: {
      type: Object,
      required: true,
    },
    isSelectable: {
      type: Boolean,
      default: false,
    },
    preventUnitSelection: {
      type: Boolean,
      default: false,
    },
    // Optional props to override computed values when needed
    externalKpiData: {
      type: Array,
      default: null,
    },
    externalUnitComplete: {
      type: [Boolean, null],
      default: null,
    },
    collapsedView: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('context', ['isUnitManager']),
    ...mapGetters('hierarchy', ['getHierarchyForWorkpackage']),
    ...mapState('workpackages', ['numberOfPriceChangesByWpIdAndCategoryId']),
    ...mapState('clientConfig', ['userRoles']),

    unitCategories() {
      return this.getHierarchyForWorkpackage(
        {
          level: this.unit.level + 1,
          parentId: this.unit.levelEntryKey,
        },
        this.unit.workpackageId
      );
    },

    kpiData() {
      const catManGreen = colours.catManGreen;
      const successGreen = colours.successGreen;
      const nullColor = colours.lightOnyx;

      return [
        {
          number: this.numberOfCategories,
          tooltipMessage: this.$t('workPackages.categoriesInScope'),
          rejected: false,
          shape: 'circle',
          bgColor: this.numberOfCategories === 0 ? nullColor : successGreen,
          textColor: 'white',
        },
        {
          number: this.numberOfReleasedCategories,
          tooltipMessage: this.$t('workPackages.numberOfCategoriesReleased'),
          rejected: false,
          shape: 'square',
          bgColor: 'transparent',
          textColor: successGreen,
          outlineColor: successGreen,
        },
        {
          ...this.categoriesApprovedByCm,
          userRole: get(this, 'userRoles.categoryManager'),
          shape: 'circle',
          bgColor: this.getApprovalColor(this.categoriesApprovedByCm, catManGreen),
          textColor: 'black',
        },
        {
          ...this.categoriesApprovedByPs,
          shape: 'circle',
          bgColor: this.getApprovalColor(this.categoriesApprovedByPs, successGreen),
          textColor: 'black',
        },
        {
          number: this.numPriceChanges,
          tooltipMessage: this.$t('workPackages.numberOfPriceChangesFromApprovedCategories'),
          shape: 'pill',
          bgColor: 'white',
          outlineColor: 'black',
          textColor: 'black',
        },
      ];
    },

    numberOfCategories() {
      return size(this.unitCategories);
    },

    categoriesApprovedByCm() {
      return this.categoriesApproved('categoryManagerApproval', 'Cm');
    },

    categoriesApprovedByPs() {
      return this.categoriesApproved('pricingSpecialistApproval', 'Ps');
    },

    numberOfReleasedCategories() {
      let pricingGroups;
      let allReleased;
      const result = this.unitCategories.map(category => {
        pricingGroups = this.getHierarchyForWorkpackage(
          pg => pg.parentId === category.levelEntryKey,
          this.unit.workpackageId
        );
        if (isEmpty(pricingGroups)) return false;
        allReleased = every(pricingGroups, pg => get(pg, 'released.isReleased', null) === true);
        return allReleased;
      });
      return size(filter(result, res => res === true));
    },

    numPriceChanges() {
      return this.unitCategories.reduce((priceChanges, category) => {
        const pricingGroups = this.getHierarchyForWorkpackage(
          pg => pg.parentId === category.levelEntryKey,
          this.unit.workpackageId
        );
        if (isEmpty(pricingGroups)) return priceChanges + 0;
        const allApprovedByPs = every(
          pricingGroups,
          pg => get(pg, 'approvalStatus.pricingSpecialistApproval.approved', null) === true
        );
        const someRejectedByCm = some(
          pricingGroups,
          pg => get(pg, 'approvalStatus.categoryManagerApproval.approved', null) === false
        );
        if (allApprovedByPs && !someRejectedByCm) {
          return (
            priceChanges +
            get(
              this.numberOfPriceChangesByWpIdAndCategoryId,
              `${this.unit.workpackageId}::${category.levelEntryKey}`,
              0
            )
          );
        }
        return priceChanges;
      }, 0);
    },

    unitComplete() {
      // Use external value if provided
      if (this.externalUnitComplete !== null) {
        return this.externalUnitComplete;
      }

      const categoryNumber = this.numberOfCategories;
      const cmApproval = this.categoriesApprovedByCm;
      const cmApprovalNumber = cmApproval.rejected ? 0 : cmApproval.number;
      const psApproval = this.categoriesApprovedByPs;
      const psApprovalNumber = psApproval.rejected ? 0 : psApproval.number;
      if (categoryNumber === cmApprovalNumber && categoryNumber === psApprovalNumber) return true;
      return null;
    },
  },

  methods: {
    ...mapActions('workpackages', ['setUserSelectionUnit']),

    async selectUnit() {
      if (this.preventUnitSelection) return;

      this.$emit('select-unit', this.unit);

      const selectedWorkpackageId = this.unit.workpackageId;
      const unitLevelEntryKey = this.unit.levelEntryKey;
      const parentLevelEntryKey = this.unit.parentId;

      await this.setUserSelectionUnit({
        selectedWorkpackageId,
        unitLevelEntryKey,
        parentLevelEntryKey,
      });

      if (this.isUnitManager) {
        this.$router.push({ path: `pricing/impact-view/workpackage/${selectedWorkpackageId}` });
      } else {
        this.$router.push({ path: `pricing/desk-view/workpackage/${selectedWorkpackageId}` });
      }
    },

    categoriesApproved(approvalType, messageType) {
      let pricingGroups;
      let allApproved;
      let allNull;
      let someRejected;

      const result = this.unitCategories.map(category => {
        pricingGroups = this.getHierarchyForWorkpackage(
          pg => pg.parentId === category.levelEntryKey,
          this.unit.workpackageId
        );

        if (isEmpty(pricingGroups))
          return { allApproved: false, someRejected: false, allNull: true };

        allApproved = every(
          pricingGroups,
          pg => get(pg, `approvalStatus.${approvalType}.approved`, null) === true
        );

        someRejected = some(
          pricingGroups,
          pg => get(pg, `approvalStatus.${approvalType}.approved`, null) === false
        );

        allNull = every(
          pricingGroups,
          pg => get(pg, `approvalStatus.${approvalType}.approved`, null) === null
        );

        return { allApproved, allNull, someRejected };
      });

      const approvalCounts = size(filter(result, { allApproved: true }));
      const rejectedCounts = size(filter(result, { someRejected: true }));
      const nullCounts = size(filter(result, { allNull: true }));

      if (nullCounts === size(this.unitCategories))
        return {
          number: 0,
          rejected: false,
          tooltipMessage: this.$t(`workPackages.numberOfCategoriesApprovedBy${messageType}`),
        };

      if (rejectedCounts)
        return {
          number: rejectedCounts,
          rejected: true,
          tooltipMessage: this.$t(`workPackages.numberOfCategoriesRejectedBy${messageType}`),
        };

      return {
        number: approvalCounts,
        rejected: false,
        tooltipMessage: this.$t(`workPackages.numberOfCategoriesApprovedBy${messageType}`),
      };
    },

    getApprovalColor(data, defaultColor) {
      if (data.number === 0) return colours.lightOnyx;
      if (data.rejected) return 'red';
      return defaultColor;
    },
  },
};
</script>

<style lang="scss" scoped>
.pricing-group-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #023b6e;
  cursor: pointer;
}
</style>
